import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDisplayDate } from '@/utils/dateUtils';
import {
  Truck, Wrench, DollarSign, Gauge, MapPin, Layers, CalendarRange, Loader2, AlertTriangle,
  Activity, TrendingDown, Clock, PackageX,
} from 'lucide-react';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ZONE_COLORS = ['#0e7490', '#0891b2', '#0d9488', '#15803d', '#65a30d', '#ca8a04', '#ea580c', '#9333ea', '#475569', '#0369a1'];

const fmtMoney = (v, currency = 'AUD') => {
  if (v == null) return '-';
  try {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `$${Math.round(v).toLocaleString()}`;
  }
};

const Sparkline = ({ values = [], color = '#0e7490' }) => {
  const max = Math.max(1, ...values);
  return (
    <div className="flex items-end gap-[1px] h-6" title={`Last ${values.length} days operating hours`}>
      {values.map((v, i) => (
        <div key={i} className="w-[3px] rounded-sm" style={{ height: `${Math.max(8, (v / max) * 100)}%`, backgroundColor: v > 0.5 ? color : '#cbd5e1' }} />
      ))}
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, sub, tone = 'slate', testid }) => {
  const tones = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
          <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md border ${tones[tone]}`}>
            <Icon className="h-4 w-4" />
          </span>
        </div>
        <div className="mt-2 text-2xl font-bold text-slate-900">{value}</div>
        {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
      </CardContent>
    </Card>
  );
};

export default function ProjectDashboard({ projectId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return;
    let active = true;
    setLoading(true);
    setData(null);
    axios.get(`${API}/projects/${projectId}/equipment-dashboard`, { headers: getAuthHeader() })
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, projectId]);

  const currency = data?.currency || 'AUD';
  const s = data?.summary || {};
  const u = data?.usage || {};

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-4xl lg:max-w-6xl xl:max-w-[1500px] overflow-y-auto" data-testid="project-dashboard-sheet">
        <SheetHeader>
          <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Gauge className="h-6 w-6 text-cyan-600" />
            {data?.project?.name || 'Project Dashboard'}
          </SheetTitle>
          <SheetDescription>Equipment hire overview for this project</SheetDescription>
          {data?.project && (
            <div className="flex items-center gap-2 pt-1">
              <Badge className="bg-cyan-50 text-cyan-700 border-cyan-200">{data.project.status}</Badge>
              <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                <CalendarRange className="h-3.5 w-3.5" />
                {formatDisplayDate(data.project.start_date)} – {formatDisplayDate(data.project.end_date)}
              </span>
            </div>
          )}
        </SheetHeader>

        {loading && (
          <div className="flex items-center justify-center py-24 text-slate-400" data-testid="project-dashboard-loading">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading dashboard…
          </div>
        )}

        {!loading && !data && (
          <div className="py-24 text-center text-slate-500">Unable to load dashboard data.</div>
        )}

        {!loading && data && (
          <div className="space-y-6 py-5">
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <Kpi icon={Truck} label="On Hire Now" tone="cyan" testid="kpi-on-hire-now"
                value={s.on_hire_now} sub={`of ${s.total_items} items used on project`} />
              <Kpi icon={Gauge} label="Fleet Utilisation" tone="emerald" testid="kpi-utilisation"
                value={`${s.utilisation_pct ?? 0}%`} sub={`peak ${s.peak_on_hire ?? 0} on hire`} />
              <Kpi icon={Wrench} label="Service Due (30d)" tone={s.due_for_service_30d ? 'red' : 'slate'} testid="kpi-service-due"
                value={s.due_for_service_30d} sub="upcoming or overdue" />
              <Kpi icon={DollarSign} label="Hire Burn / Month" tone="amber" testid="kpi-monthly-cost"
                value={fmtMoney(s.monthly_hire_cost, currency)} sub="current on-hire fleet" />
              <Kpi icon={DollarSign} label="Committed Hire" tone="slate" testid="kpi-committed-cost"
                value={fmtMoney(s.committed_hire_cost, currency)} sub="across project duration" />
              <Kpi icon={Layers} label="Plant Categories" tone="slate" testid="kpi-categories"
                value={(data.by_category || []).length} sub="on hire now" />
            </div>

            {/* Usage & idle (actual operating hours) */}
            {u.has_usage_data ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  <Kpi icon={Activity} label="Actual Utilisation" tone="emerald" testid="kpi-actual-utilisation"
                    value={`${u.fleet_actual_utilisation_pct ?? 0}%`} sub={`operating hrs vs ${u.baseline_hours_per_day}h/day · last ${u.window_days}d`} />
                  <Kpi icon={Clock} label="Idle Days" tone={u.total_idle_days ? 'amber' : 'slate'} testid="kpi-idle-days"
                    value={u.total_idle_days} sub={`on-hire days with ~0 use · last ${u.window_days}d`} />
                  <Kpi icon={TrendingDown} label="Potential Saving / Month" tone={u.potential_monthly_saving ? 'red' : 'emerald'} testid="kpi-potential-saving"
                    value={fmtMoney(u.potential_monthly_saving, currency)} sub={`${(u.off_hire_candidates || []).length} off-hire candidates`} />
                </div>

                {/* Off-hire candidates */}
                <Card className="border-amber-200 bg-amber-50/40">
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2">
                      <PackageX className="h-4 w-4 text-amber-600" /> Off-Hire Candidates
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200">{(u.off_hire_candidates || []).length}</Badge>
                    </h3>
                    <p className="text-xs text-slate-500 mb-3">On hire but averaging under {u.idle_threshold_hours} hrs/day over the last {u.idle_lookback_days} days — review for off-hire.</p>
                    {(u.off_hire_candidates || []).length === 0 ? (
                      <p className="text-sm text-emerald-700 py-4 text-center">No idle assets — the fleet is working. 🎉</p>
                    ) : (
                      <div className="overflow-x-auto" data-testid="off-hire-candidates">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Tag</TableHead>
                              <TableHead className="text-xs">Item</TableHead>
                              <TableHead className="text-xs">Site Zone</TableHead>
                              <TableHead className="text-xs text-right">Avg hrs/day (14d)</TableHead>
                              <TableHead className="text-xs text-right">Hire / day</TableHead>
                              <TableHead className="text-xs text-right">Est. saving / mo</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {u.off_hire_candidates.map((c) => (
                              <TableRow key={c.equipment_id} data-testid={`off-hire-row-${c.tag}`}>
                                <TableCell className="text-xs font-mono">{c.tag}</TableCell>
                                <TableCell className="text-sm">{c.name}<span className="block text-[10px] text-slate-400">{c.category}</span></TableCell>
                                <TableCell className="text-xs">{c.zone || '-'}</TableCell>
                                <TableCell className="text-right"><Badge className="bg-red-50 text-red-700 border-red-200">{c.avg_hours_day}h</Badge></TableCell>
                                <TableCell className="text-right text-xs">{fmtMoney(c.daily_rate, currency)}</TableCell>
                                <TableCell className="text-right text-sm font-semibold text-emerald-700">{fmtMoney(c.est_monthly_saving, currency)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Fleet operating hours trend */}
                <Card className="border-slate-200">
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Activity className="h-4 w-4 text-cyan-600" /> Fleet Operating Hours (last 12 weeks)
                    </h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={u.usage_trend} margin={{ left: 0, right: 12 }}>
                        <defs>
                          <linearGradient id="usageFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#0d9488" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#0d9488" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" tick={{ fontSize: 10 }} minTickGap={15} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => `${v} hrs`} />
                        <Area type="monotone" dataKey="operating_hours" stroke="#0d9488" strokeWidth={2} fill="url(#usageFill)" name="Operating hrs" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="border-dashed border-slate-300 bg-slate-50">
                <CardContent className="p-4 text-sm text-slate-500 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-slate-400" />
                  No usage data yet. Connect a GPS provider or import operating hours to unlock actual utilisation & off-hire insights.
                </CardContent>
              </Card>
            )}

            {/* On hire by category + by zone */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Layers className="h-4 w-4 text-cyan-600" /> On Hire by Plant Category
                  </h3>
                  {(data.by_category || []).length === 0 ? (
                    <p className="text-sm text-slate-400 py-8 text-center">Nothing on hire right now.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(220, (data.by_category.length) * 30)}>
                      <BarChart data={data.by_category} layout="vertical" margin={{ left: 30, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#0e7490" radius={[0, 4, 4, 0]} name="On hire" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-cyan-600" /> Where On Site (Site Zone)
                  </h3>
                  {(data.by_zone || []).length === 0 ? (
                    <p className="text-sm text-slate-400 py-8 text-center">No zone data.</p>
                  ) : (
                    <div className="space-y-2" data-testid="dashboard-zone-breakdown">
                      {data.by_zone.map((z, i) => {
                        const max = data.by_zone[0]?.count || 1;
                        return (
                          <div key={z.label} className="flex items-center gap-3">
                            <span className="w-32 truncate text-xs text-slate-600" title={z.label}>{z.label}</span>
                            <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                              <div className="h-full rounded flex items-center justify-end pr-2"
                                style={{ width: `${Math.max(8, (z.count / max) * 100)}%`, backgroundColor: ZONE_COLORS[i % ZONE_COLORS.length] }}>
                                <span className="text-[10px] font-semibold text-white">{z.count}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Hire trend over the job */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <CalendarRange className="h-4 w-4 text-cyan-600" /> Plant On Hire Over the Program
                </h3>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={data.hire_trend} margin={{ left: 0, right: 12 }}>
                    <defs>
                      <linearGradient id="hireFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0e7490" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#0e7490" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={20} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v, n) => n === 'cost' ? fmtMoney(v, currency) : v} />
                    <Area type="monotone" dataKey="on_hire" stroke="#0e7490" strokeWidth={2} fill="url(#hireFill)" name="On hire" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Service due list */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-red-500" /> Service Due / Overdue
                  <Badge variant="secondary" className="text-xs">{(data.service_due || []).length}</Badge>
                </h3>
                {(data.service_due || []).length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center">No services due in the next 30 days. 🎉</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Tag</TableHead>
                        <TableHead className="text-xs">Item</TableHead>
                        <TableHead className="text-xs">Site Zone</TableHead>
                        <TableHead className="text-xs">Due</TableHead>
                        <TableHead className="text-xs">When</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.service_due.map((it) => (
                        <TableRow key={it.equipment_id} data-testid={`service-due-row-${it.tag}`}>
                          <TableCell className="text-xs font-mono">{it.tag}</TableCell>
                          <TableCell className="text-sm">{it.name}<span className="block text-[10px] text-slate-400">{it.category}</span></TableCell>
                          <TableCell className="text-xs">{it.zone || '-'}</TableCell>
                          <TableCell className="text-xs">{it.next_maintenance_date ? formatDisplayDate(it.next_maintenance_date) : '-'}</TableCell>
                          <TableCell className="text-xs">
                            {it.days_until < 0 ? (
                              <Badge className="bg-red-50 text-red-700 border-red-200"><AlertTriangle className="h-3 w-3 mr-1" />{Math.abs(it.days_until)}d overdue</Badge>
                            ) : it.days_until === 0 ? (
                              <Badge className="bg-amber-50 text-amber-700 border-amber-200">Due today</Badge>
                            ) : (
                              <Badge className="bg-slate-50 text-slate-600 border-slate-200">in {it.days_until}d</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Per-asset usage / utilisation */}
            {u.has_usage_data && (u.per_asset || []).length > 0 && (
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Gauge className="h-4 w-4 text-cyan-600" /> Asset Usage (last {u.window_days} days)
                    <span className="text-xs font-normal text-slate-400">lowest utilisation first</span>
                  </h3>
                  <div className="overflow-x-auto" data-testid="per-asset-usage">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Tag</TableHead>
                          <TableHead className="text-xs">Item</TableHead>
                          <TableHead className="text-xs">Zone</TableHead>
                          <TableHead className="text-xs text-right">Hrs</TableHead>
                          <TableHead className="text-xs text-right">Avg/day</TableHead>
                          <TableHead className="text-xs text-right">Util %</TableHead>
                          <TableHead className="text-xs text-right">Idle days</TableHead>
                          <TableHead className="text-xs">Last active</TableHead>
                          <TableHead className="text-xs">Trend (14d)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {u.per_asset.filter(r => r.on_hire_now).map((r) => {
                          const tone = r.utilisation_pct < 25 ? 'bg-red-50 text-red-700 border-red-200'
                            : r.utilisation_pct < 60 ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-emerald-50 text-emerald-700 border-emerald-200';
                          return (
                            <TableRow key={r.equipment_id} data-testid={`usage-row-${r.tag}`}>
                              <TableCell className="text-xs font-mono">{r.tag}</TableCell>
                              <TableCell className="text-sm">{r.name}<span className="block text-[10px] text-slate-400">{r.category}</span></TableCell>
                              <TableCell className="text-xs">{r.zone || '-'}</TableCell>
                              <TableCell className="text-right text-xs">{r.operating_hours}</TableCell>
                              <TableCell className="text-right text-xs">{r.avg_hours_day}</TableCell>
                              <TableCell className="text-right"><Badge className={tone}>{r.utilisation_pct}%</Badge></TableCell>
                              <TableCell className="text-right text-xs">{r.idle_days}</TableCell>
                              <TableCell className="text-xs">{r.last_active ? formatDisplayDate(r.last_active) : '—'}</TableCell>
                              <TableCell><Sparkline values={r.sparkline} /></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
